<template>
  <div v-show="viewedIntro" class="main-container">
    <portal
      v-if="
        viewedIntro && isAssessmentAcceleratorActivity() && !isAssessmentAcceleratorTesterActivity
      "
      to="save-activity-button"
    >
      <save-activity-button></save-activity-button>
    </portal>

    <portal
      v-if="
        viewedIntro &&
          isAssessmentAcceleratorActivity() &&
          !isAssessmentAcceleratorTesterActivity &&
          assessmentAcceleratorModuleProgress
      "
      to="activity-progress-bar"
    >
      <activity-progress-bar
        :activityHasInstructions="!!(instructions && instructions.text)"
        :currentActivity="assessmentAcceleratorModuleProgress.currentActivity"
        :totalActivities="assessmentAcceleratorModuleProgress.totalActivities"
      ></activity-progress-bar>
    </portal>

    <div
      :class="{
        'component-container': isAssessmentAcceleratorActivity(),
        tablet: isMobile,
        'has-progress-bar':
          viewedIntro &&
          isAssessmentAcceleratorActivity() &&
          !isAssessmentAcceleratorTesterActivity &&
          assessmentAcceleratorModuleProgress
      }"
    >
      <accessibility-text-helper
        v-if="shouldShowAccessibilityTextHelper"
      ></accessibility-text-helper>
      <component :is="activity" class="activity-template" ref="activity">
        <template v-if="instructions && instructions.text" v-slot:instructions="{ onVoiced }">
          <activity-instructions
            :instructions="instructions"
            :onInstructionsVoiced="onVoiced"
          ></activity-instructions>
        </template>
        <component :is="keyboardComponent" slot="keyboard"></component>
      </component>
    </div>

    <portal
      v-if="viewedIntro && isAssessmentAcceleratorActivity() && !hasCustomNextButton"
      to="next-activity-button"
    >
      <next-activity-button :on-click="handleOnActivitySubmit"></next-activity-button>
    </portal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { disabledTextHelperActivities } from 'constants/accessibility-text-helper';
import { createAudio } from 'utils/audio-util';
import StandardKeyboard from '../keyboards/StandardKeyboard.vue';
import ExternalKeyboard from '../keyboards/ExternalKeyboard.vue';
import MobileOnscreenKeyboard from '../keyboards/MobileOnscreenKeyboard.vue';
import AccessibilityTextHelper from './AccessibilityTextHelper.vue';
import ActivityInstructions from './ActivityInstructions.vue';
import ActivityProgressBar from './ActivityProgressBar.vue';
import NextActivityButton from './NextActivityButton.vue';
import SaveActivityButton from './SaveActivityButton.vue';
import MetaHelper from 'mixins/MetaHelper';

export default {
  mixins: [MetaHelper],

  components: {
    AccessibilityTextHelper,
    ActivityProgressBar,
    ExternalKeyboard,
    MobileOnscreenKeyboard,
    NextActivityButton,
    SaveActivityButton,
    StandardKeyboard,
    ActivityInstructions
  },
  computed: {
    ...mapState('application', ['device']),
    ...mapState('activity', [
      'progress',
      'instructions',
      'viewedIntro',
      'data',
      'dataLoaded',
      'readyToStart'
    ]),
    ...mapState('student', ['accessibilitySettings']),

    ...mapGetters('activity', [
      'assessmentAcceleratorModuleProgress',
      'activityTitle',
      'hasCustomNextButton'
    ]),
    ...mapGetters('activity/typing', ['isTouchHold']),
    ...mapGetters('application', ['needsToChooseKeyboard']),

    ...mapState('keyboard', {
      isTouchHold: ({ settings: { isTouchHold } = {} }) => isTouchHold,
      keyboardEnabled: state => state.enabled
    }),
    ...mapState('application', {
      isMobile: state => state.device.isMobile,
      kwtMode: state => state.kwtMode
    }),
    ...mapGetters('application', ['usingExternalKeyboard']),

    activity() {
      const { activityTitle: { name: activityTitle } = {} } = this;

      return () => import(`@/activities/${activityTitle}.vue`);
    },

    keyboardComponent() {
      const { isMobile, usingExternalKeyboard } = this;
      let name = 'StandardKeyboard';
      if (isMobile) {
        name = usingExternalKeyboard ? 'ExternalKeyboard' : 'MobileOnscreenKeyboard';
      }

      return name;
    },

    isAssessmentAcceleratorTesterActivity() {
      return !!(
        this.isAssessmentAcceleratorActivity() &&
        // (this.kwtMode === 'tester' || ['preview', 'previewLite'].includes(this.kwtMode))
        ['preview', 'previewLite'].includes(this.kwtMode)
      );
    },

    shouldShowAccessibilityTextHelper() {
      return (
        !!this.data.keyboard &&
        this.keyboardEnabled &&
        !disabledTextHelperActivities[this.data.type]
      );
    }
  },

  methods: {
    ...mapGetters('activity', ['isAssessmentAcceleratorActivity']),
    handleOnActivitySubmit() {
      this.$refs.activity.handleOnActivitySubmit();
    }
  },

  watch: {
    extKeyAttemptEvent(newEvent) {
      this.handleKeyDown(newEvent);
    },

    voiceInstructions(path) {
      if (path) {
        this.voiceIntro = createAudio(path, {
          html5: !this.device.isMobile,
          volume: this.accessibilitySettings.audioSettings.muteSounds
            ? 0
            : this.accessibilitySettings.audioSettings.volume * 0.01
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.activity-template {
  height: 100%;
}

.main-container {
  height: 100%;
  overflow: hidden;
  position: relative;
}

.component-container {
  height: 100%;
}

#screenReaderAlert {
  bottom: 0;
  height: 1px;
  position: absolute;
  opacity: 0;
  width: 1px;
}
</style>
